<script setup lang="ts">
import { CirclePlus, Delete, Edit } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { onMounted, ref, watch } from "vue";
import {
  EmailStyleTemplateDto,
  EmailTemplateDto,
  StatusChangeActions,
  WorkflowDefinition,
  WorkflowDefinitionListDto,
  WorkflowStatusChangeActionType,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { workflowStatusChangeActionDisplayNames } from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
import ChangeEventCreator from "./ChangeEventCreator.vue";

type listItemType = WorkflowDefinition;

const myInfoStore = useMyInfoStore();

const props = defineProps<{
  workflowDefinition: listItemType;
  readOnly?: boolean;
}>();

const emit = defineEmits(["updated"]);

// Helper function to convert GUID to question code/label
const getQuestionDisplayName = (label: string): string => {
  // If it's not a valid GUID format, it's probably an email address, return as is
  if (
    !label ||
    !label.match(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    )
  ) {
    return label;
  }

  // Search through all form definitions to find the question with this GUID
  for (const form of props.workflowDefinition.formDefinitions || []) {
    for (const component of form.components || []) {
      if (component.guid === label) {
        return `${form.formCode || form.guid.slice(0, 8)}: ${component.questionCode}`;
      }
    }
  }

  // If GUID not found in form definitions, return the GUID (fallback)
  return label;
};

const allWorkflowDefinitions = ref<WorkflowDefinitionListDto[]>([]);

const loadWorkflowDefinitions = async () => {
  try {
    allWorkflowDefinitions.value =
      await apiClient.workflowDefinition.getAllWorkflowDefinitionDtos(
        myInfoStore.currentSysOrgGuid,
      );
  } catch (error) {
    showError("Failed to load workflow definitions:", error);
  }
};

const getTargetDefinitionName = (guid?: string | null): string => {
  if (!guid) return "Same definition";
  const def = allWorkflowDefinitions.value.find((d) => d.guid === guid);
  return def?.name || guid;
};

const isFollowUpAction = (row: StatusChangeActions): boolean =>
  row.actionType === WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW;

const statusChangeActions = ref<StatusChangeActions[]>([]);
const changeEventDialogOpen = ref(false);
const changeEventOpenCounter = ref(0);
const eventToEdit = ref<StatusChangeActions>();
const editingIndex = ref<number>();

const createChangeEvent = () => {
  eventToEdit.value = undefined;
  editingIndex.value = undefined;
  changeEventOpenCounter.value++;
  changeEventDialogOpen.value = true;
};

const handleAddStatusChangeAction = (action: StatusChangeActions) => {
  statusChangeActions.value.push(action);
  updateWorkflowDefinition();
  ElMessage.success("Event added successfully");
};

const updateWorkflowDefinition = async () => {
  try {
    const updatedWorkflowDefinition = { ...props.workflowDefinition };

    updatedWorkflowDefinition.statusChangeActionsList =
      statusChangeActions.value;

    await apiClient.workflowDefinition.updateWorkflowDefinition(
      updatedWorkflowDefinition.guid,
      updatedWorkflowDefinition,
    );

    emit("updated", updatedWorkflowDefinition);
  } catch (error) {
    showError("Failed to update workflow definition:", error);
  }
};

const handleDeleteAction = async (index: number) => {
  statusChangeActions.value.splice(index, 1);
  await updateWorkflowDefinition();
  ElMessage.success("Event removed successfully");
};

const handleUpdateAction = async (index: number) => {
  eventToEdit.value = { ...statusChangeActions.value[index] };
  editingIndex.value = index;
  changeEventOpenCounter.value++;
  changeEventDialogOpen.value = true;
};

const handleUpdateStatusChangeAction = async (
  action: StatusChangeActions,
  index: number,
) => {
  statusChangeActions.value[index] = action;
  await updateWorkflowDefinition();
  ElMessage.success("Event updated successfully");
};

const allEmailTemplates = ref<EmailTemplateDto[]>([]);
const allStyleTemplates = ref<EmailStyleTemplateDto[]>([]);
const loading = ref(true);

const getAllEmailTemplates = async () => {
  loading.value = true;
  await apiClient.emailTemplates
    .getAllEmailTemplates(
      myInfoStore.currentSysOrgGuid,
      props.workflowDefinition.guid,
      true,
    )
    .then((res) => {
      allEmailTemplates.value = res;
      loading.value = false;
    });
};

const getEmailTemplateDisplayName = (
  emailTemplateFormResponseGuid: string,
): string => {
  const template = allEmailTemplates.value.find(
    (t) => t.formResponseGuid === emailTemplateFormResponseGuid,
  );
  return template ? template.name : emailTemplateFormResponseGuid;
};

const getStyleTemplateDisplayName = (
  styleTemplateFormResponseGuid: string,
): string => {
  const template = allStyleTemplates.value.find(
    (t) => t.formResponseGuid === styleTemplateFormResponseGuid,
  );
  return template ? template.name : styleTemplateFormResponseGuid;
};

const getAllStyleEmailTemplates = async () => {
  loading.value = true;
  await apiClient.emailTemplates
    .getAllEmailStyleTemplates(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      allStyleTemplates.value = res;
      loading.value = false;
    });
};

watch(
  () => props.workflowDefinition,
  (newVal) => {
    if (newVal) {
      statusChangeActions.value = newVal.statusChangeActionsList ?? [];
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await Promise.all([
    getAllEmailTemplates(),
    getAllStyleEmailTemplates(),
    loadWorkflowDefinitions(),
  ]);

  statusChangeActions.value =
    props.workflowDefinition.statusChangeActionsList ?? [];
});
</script>

<template>
  <div class="EventListing">
    <div class="header-container">
      <h3>Change Events</h3>
      <el-button
        :icon="CirclePlus"
        title="Add New Workflow Step"
        class="addButton"
        :disabled="props.readOnly"
        @click="createChangeEvent()"
      >
        New Change Event</el-button
      >
    </div>
    <div class="EventListingSection">
      <el-table :data="statusChangeActions" style="width: 100%">
        <el-table-column label="Status To">
          <template #default="{ row }">
            {{ row.newStatus?.label || "-" }}
          </template>
        </el-table-column>

        <el-table-column label="Status From">
          <template #default="{ row }">
            {{ row.oldStatus?.label || "-" }}
          </template>
        </el-table-column>
        <el-table-column label="Event Code">
          <template #default="{ row }">
            {{ row.eventCode || "-" }}
          </template>
        </el-table-column>

        <el-table-column label="Action Type">
          <template #default="{ row }">
            {{
              workflowStatusChangeActionDisplayNames[
                row.actionType as keyof typeof workflowStatusChangeActionDisplayNames
              ]
            }}
          </template>
        </el-table-column>

        <el-table-column label="Send To">
          <template #default="{ row }">
            <template v-if="!isFollowUpAction(row)">
              {{ row.sendTo?.map(getQuestionDisplayName).join(", ") }}
            </template>
            <template v-else>-</template>
          </template>
        </el-table-column>

        <el-table-column label="Send From">
          <template #default="{ row }">
            <template v-if="!isFollowUpAction(row)">
              {{ row.sendFrom }}
            </template>
            <template v-else>-</template>
          </template>
        </el-table-column>

        <el-table-column label="Email Template">
          <template #default="{ row }">
            <template v-if="!isFollowUpAction(row)">
              {{
                getEmailTemplateDisplayName(row.emailTemplateFormResponseGuid)
              }}
            </template>
            <template v-else>-</template>
          </template>
        </el-table-column>

        <el-table-column label="Style Template">
          <template #default="{ row }">
            <template v-if="!isFollowUpAction(row)">
              {{
                getStyleTemplateDisplayName(row.styleTemplateFormResponseGuid)
              }}
            </template>
            <template v-else>-</template>
          </template>
        </el-table-column>

        <el-table-column label="Target Definition">
          <template #default="{ row }">
            <template v-if="isFollowUpAction(row)">
              {{ getTargetDefinitionName(row.targetWorkflowDefinitionGuid) }}
              <span v-if="row.defaultAnswers?.length">
                ({{ row.defaultAnswers.length }} default answer{{
                  row.defaultAnswers.length > 1 ? "s" : ""
                }})
              </span>
            </template>
            <template v-else>-</template>
          </template>
        </el-table-column>

        <el-table-column label="Actions">
          <template #default="{ $index }">
            <div style="display: flex">
              <el-button
                :disabled="props.readOnly"
                :icon="Edit"
                size="small"
                @click="() => handleUpdateAction($index)"
              />
              <el-button
                :disabled="props.readOnly"
                :icon="Delete"
                size="small"
                @click="() => handleDeleteAction($index)"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <ChangeEventCreator
      v-if="changeEventDialogOpen"
      :key="changeEventOpenCounter"
      v-model="changeEventDialogOpen"
      :user-guid="myInfoStore.userInfo.userGuid"
      :organization-guid="myInfoStore.currentSysOrgGuid"
      :email-template-dtos="allEmailTemplates"
      :style-template-dtos="allStyleTemplates"
      :workflow-definition="props.workflowDefinition"
      :event-to-edit="eventToEdit"
      :editing-index="editingIndex"
      @add:status-change-action="handleAddStatusChangeAction"
      @update:status-change-action="handleUpdateStatusChangeAction"
    />
  </div>
</template>

<style lang="less" scoped>
.EventListing {
  overflow-y: auto;
  height: 100%;
}
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.EventListingSection {
  .el-form {
    width: 70%;
    margin: 0 auto 20px;
    padding: 5px 10px 2px;
    border: 2px solid #ebeef5;
    display: grid;
    grid-template-columns: 2fr 2fr auto;
    align-items: center;
    justify-content: space-between;
    grid-gap: 1em;
  }
  .create-button {
    .el-form-item__content {
      justify-content: end;
    }
  }
}
</style>
